import { Children, type ReactNode } from 'react';
import { View, StyleSheet } from 'react-native';
import { useTheme, SPACING, BORDER_RADIUS } from '../../theme';
import { BUNDLE_MEMBERS_RIGHT_INSET, BUNDLE_NAME_INSET } from './DefensePlanBundleRow.web';
import { FEATURE_ROW_NESTED_LEADING } from './DefensePlanFeatureRow.web';

interface DefensePlanBundleGroupProps {
  /** Focus-colored card edge while the bundle is the selected one. */
  selected: boolean;
  expanded: boolean;
  /** The bundle's DefensePlanBundleRow. */
  header: ReactNode;
  /** One DefensePlanFeatureRow per member, each with `nested`. */
  children: ReactNode;
  /** Set by the editor panel: the bundle's id, so a dragged defense can be
   *  dropped on the card (usePlanListDrag.web.ts). Omitted in the viewers. */
  dropId?: string;
  /** A drop here would change something: focus-colored edge and a fill. */
  dropTarget?: boolean;
}

/**
 * One bundle as one object (Sept 24 2026: Glenn said the members under a
 * bundle didn't read as grouped). A single card holds the header and, when
 * expanded, the members on an inset surface, indented so each member's
 * badge starts under the bundle name, with hairlines between them. Member
 * rows draw no border of their own (`nested`), so this is one card, not a
 * card holding cards. Shared by the editor panel and both read-only viewers
 * so a bundle looks the same everywhere.
 *
 * Sept 25 2026: the connector line that hung off the chevron is gone. It
 * sat 2px left of the chevron's center, started 36px below it, and stood
 * beside a second grey bar from the member rows; Jason found it out of
 * place. The indent alone says "inside this bundle", and it gives the name,
 * the header's badges, its summary and every member badge one left edge.
 */
export function DefensePlanBundleGroup({
  selected,
  expanded,
  header,
  children,
  dropId,
  dropTarget = false,
}: DefensePlanBundleGroupProps) {
  const { colors, isDark } = useTheme();
  // The members sit on a surface a step back from the panel's
  // (surfaceElevated): surface in light, background in dark. surfaceAlt was
  // tried first and is invisible against the dark panel (#141413 on
  // #171716).
  const insetSurface = isDark ? colors.background : colors.surface;
  const members = Children.toArray(children);
  return (
    <View
      style={[
        styles.card,
        { borderColor: selected || dropTarget ? colors.borderFocus : colors.border },
      ]}
      // @ts-expect-error dataSet is a react-native-web prop; not in RN's type defs.
      dataSet={dropId ? { planDrop: `bundle:${dropId}` } : undefined}>
      {header}
      {expanded && members.length > 0 && (
        <View
          style={[
            styles.members,
            { borderTopColor: colors.border, backgroundColor: insetSurface },
          ]}>
          {members.map((member, i) => (
            <View
              key={i}
              style={i > 0 ? [styles.divider, { borderTopColor: colors.border }] : undefined}>
              {member}
            </View>
          ))}
        </View>
      )}
      {dropTarget && (
        <View
          pointerEvents="none"
          testID={dropId ? `plan-bundle-drop-${dropId}` : undefined}
          style={[styles.dropFill, { backgroundColor: colors.hoverSurface }]}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  dropFill: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  card: {
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.md,
    overflow: 'hidden',
  },
  // The left indent puts member badges under the bundle name. The small
  // right inset keeps a member's fill a pill inside the card rather than
  // running into its edge; the header's Assign slot allows for it
  // (BUNDLE_MEMBERS_RIGHT_INSET), so both Assigns sit in one column.
  members: {
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingVertical: SPACING.xs,
    paddingLeft: BUNDLE_NAME_INSET - FEATURE_ROW_NESTED_LEADING,
    paddingRight: BUNDLE_MEMBERS_RIGHT_INSET,
  },
  divider: {
    borderTopWidth: StyleSheet.hairlineWidth,
  },
});
